/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { createContext, useContext, useEffect, useState } from "react";

import { Account, AuthenticationParameters } from "msal";

import * as adminApi from "../Services/AdminApi";

import { IAdminPermissions } from "Interfaces/Admin/IAdminPermissions";

import {
  fetchMsGraph,
  msalApp,
  requiresInteraction,
  GRAPH_ENDPOINTS,
  GRAPH_REQUESTS,
} from "./AuthHelper";
import IAuthProvider from "./IAuthProviderProps";

/**
 * Enable redirect authentication flow
 */
const useRedirectFlow = true;

const DEFAULT_AUTH_STATE = {
  isUserLoggedIn: false,
  loading: true,
} as IAuthProvider;

const authContext = createContext(DEFAULT_AUTH_STATE);

const useProvideAuth = (): IAuthProvider => {
  const [adminAccount, setAdminAccount] = useState<Account>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState<boolean>(true);
  const [graphProfile, setGraphProfile] = useState<any>();
  const [adminPermissions, setAdminPermissions] = useState<any>();

  const authCallback = (error, response) => {
    if (error) {
      const errorMessage = error ? error : "Unable to acquire access token.";
      setError(errorMessage);
    }
  };

  /**
   * Signin call back
   * @method
   * @param {boolean} redirect Is redirect enabled.
   * @returns {Promise<void>} Empty promise.
   */
  const onSignIn = (redirect: boolean) => {
    if (redirect) {
      return msalApp.loginRedirect(GRAPH_REQUESTS.LOGIN);
    }
  };

  /**
   * Signout call back
   * @method
   * @returns {void}
   */
  const onSignOut = (): void => {
    msalApp.logout();
  };

  /**
   * Returns roles as list of strings.
   * @type {string[]} adminRoles
   */
  const adminRoles: string[] = !loading
    ? adminPermissions?.adminRolePermissions.map((rolePermission) => rolePermission.role)
    : [];

  /**
   * Returns if user has any roles
   * @type {boolean} hasAnyRoles
   */
  const hasAnyRoles: boolean = adminRoles && adminRoles.length > 0;

  /**
   * Checks if the admin user had the given role
   * @param {string} role name of role
   * @returns {boolean} flag if user has given role
   */
  const hasRole = (role: string): boolean =>
    adminPermissions?.adminRolePermissions.filter((rolePermission) => rolePermission.role === role)
      .length > 0;

  /**
   * Checks if the admin has permission for given tenant
   * @param {string} tenant the tenant to check permission for
   * @param {string} verifyPermission the permission being checked
   * @returns {boolean} flag indicating if user has given permission for tenant
   */
  const hasPermission = (tenant: string, verifyPermission: string): boolean =>
    adminPermissions?.adminRolePermissions.filter(
      (rolePermission) =>
        rolePermission.tenant === tenant &&
        rolePermission.permissions.filter((permission) => permission === verifyPermission).length >
          0
    ).length > 0;

  /**
   * Initial set up of Auth Provider
   */
  useEffect(() => {
    // Register error callback for redirect flow
    msalApp.handleRedirectCallback(authCallback);

    const account = msalApp.getAccount();
    setAdminAccount(account);
    if (!account) {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    /**
     * Acquires authentication token through msal (OAUTH 2.0 flow)
     * @param {AuthenticationParameters} request The request params.
     * @param {boolean} redirect Is redirect option enabled.
     * @returns {Promise<void>} Authentication token response
     */
    const acquireToken = async (
      request: AuthenticationParameters,
      redirect: boolean
    ): Promise<void> => {
      const tokenResponse = await msalApp.acquireTokenSilent(request).catch((error) => {
        // Call interactive token request method in case of acquireTokenSilent failure
        // due to consent or interaction required
        if (requiresInteraction(error.errorCode)) {
          redirect ? msalApp.acquireTokenRedirect(request) : msalApp.acquireTokenPopup(request);
        }
      });

      if (tokenResponse) {
        const graphProfile = await fetchMsGraph(
          GRAPH_ENDPOINTS.ME,
          tokenResponse.accessToken
        ).catch(() => {
          setError("Unable to fetch Graph profile.");
        });

        graphProfile && setGraphProfile(graphProfile);
      }
    };

    /**
     * Fetch application user profile after sign in.
     * @param {string}  email The signed in email.
     * @returns {Promise<void>} Empty promise.
     */
    const fetchAdminUserPermissions = async (): Promise<void> => {
      const adminUserPermissionsResult =
        adminAccount &&
        (await adminApi.getAdminPermissions(adminAccount?.userName).catch((error: Error) => {
          setError("Unable to fetch app user profile.");
          setLoading(false);
        }));

      if (adminUserPermissionsResult) {
        const adminPermissionsResult =
          adminUserPermissionsResult.parsedBody as unknown as IAdminPermissions;

        // found profile so update state accordingly.
        adminPermissionsResult && setAdminPermissions(adminPermissionsResult);
        setLoading(false);
      }
    };

    (async () => {
      await acquireToken(GRAPH_REQUESTS.LOGIN, useRedirectFlow);
      await fetchAdminUserPermissions();
    })();
  }, [adminAccount]);

  return {
    account: adminAccount,
    adminPermissions,
    error,
    graphProfile,
    hasAnyRoles,
    hasPermission,
    hasRole,
    isUserLoggedIn: !!adminAccount,
    loading,
    onSignIn,
    onSignOut,
    roles: adminRoles,
  };
};

export const AuthProvider = ({ children }): React.ReactElement => {
  const auth = useProvideAuth();
  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
};

export const useAuth = () => {
  return useContext(authContext);
};

export const withAuthProvider = (Component: any) => {
  // eslint-disable-next-line react/display-name
  return (props: any) => {
    const authProvider: IAuthProvider = useAuth();

    return <Component authProvider={authProvider} {...props} />;
  };
};
